import { useMutation, UseQueryOptions } from '@tanstack/react-query'
import axiosClient from '../../axios'
import { useGet } from '../../query/useGet'

export interface IProjectSource {
  id: string
  projectId: string
  sourceType: 'article' | 'pdf' | 'doc' | 'ppt' | 'csv' | 'xlsx' | 'note' | 'link'
  title: string
  content?: string
  filePath?: string
  fileSize?: number
  mimeType?: string
  url?: string
  metadata: Record<string, any>
  parsedContent?: string
  extractedEntities?: {
    protocols?: any[]
    methods?: any[]
    results?: any[]
    entities?: {
      pathologies?: string[]
      models?: string[]
      molecules?: string[]
      cells?: string[]
    }
    figures?: any[]
    statistics?: any[]
  }
  processingStatus: 'pending' | 'processing' | 'completed' | 'failed'
  processingError?: string
  addedBy: string
  position: number
  createdAt: string
  updatedAt: string
}

export interface ICreateSourceNote {
  title: string
  content: string
}

export interface ICreateSourceLink {
  title: string
  url: string
}

/**
 * Get all sources for a project
 * Auto-refetches every 3 seconds if any source is being processed
 */
export function useProjectSources(projectId?: string, config?: UseQueryOptions<{ sources: IProjectSource[] }>) {
  const mergedConfig = {
    ...config,
    enabled: !!projectId && (config?.enabled ?? true),
    // Auto-refresh every 3 seconds if any source is processing
    refetchInterval: (query: any) => {
      const data = query?.state?.data
      const hasProcessing = data?.sources?.some(
        (source: IProjectSource) => source.processingStatus === 'processing' || source.processingStatus === 'pending'
      )
      return hasProcessing ? 3000 : false
    },
  } as UseQueryOptions<{ sources: IProjectSource[] }>

  return useGet<{ sources: IProjectSource[] }>(
    `/projects/${projectId || 'undefined'}/sources`,
    mergedConfig
  )
}

/**
 * Get a specific source
 * Auto-refetches every 3 seconds if the source is being processed
 */
export function useProjectSource(sourceId?: string, config?: UseQueryOptions<{ source: IProjectSource }>) {
  const mergedConfig = {
    ...config,
    enabled: !!sourceId && (config?.enabled ?? true),
    // Auto-refresh every 3 seconds if source is processing
    refetchInterval: (query: any) => {
      const data = query?.state?.data
      const isProcessing =
        data?.source?.processingStatus === 'processing' ||
        data?.source?.processingStatus === 'pending'
      return isProcessing ? 3000 : false
    },
  } as UseQueryOptions<{ source: IProjectSource }>

  return useGet<{ source: IProjectSource }>(
    `/sources/${sourceId || 'undefined'}`,
    mergedConfig
  )
}

/**
 * Upload a file to a project
 */
export function useUploadSource() {
  return useMutation({
    mutationKey: ['/upload-source'],
    mutationFn: ({ projectId, file }: { projectId: string; file: File }) => {
      const formData = new FormData()
      formData.append('file', file)
      return axiosClient.post(`/projects/${projectId}/sources/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    },
  })
}

/**
 * Create a text note in a project
 */
export function useCreateNote() {
  return useMutation({
    mutationKey: ['/create-note'],
    mutationFn: ({ projectId, data }: { projectId: string; data: ICreateSourceNote }) => {
      return axiosClient.post(`/projects/${projectId}/sources/note`, data)
    },
  })
}

/**
 * Create a link in a project
 */
export function useCreateLink() {
  return useMutation({
    mutationKey: ['/create-link'],
    mutationFn: ({ projectId, data }: { projectId: string; data: ICreateSourceLink }) => {
      return axiosClient.post(`/projects/${projectId}/sources/link`, data)
    },
  })
}

/**
 * Delete a source
 */
export function useDeleteSource() {
  return useMutation({
    mutationKey: ['/delete-source'],
    mutationFn: (sourceId: string) => {
      return axiosClient.delete(`/projects/sources/${sourceId}`)
    },
  })
}

/**
 * Reprocess a source (re-parse and re-index)
 */
export function useReprocessSource() {
  return useMutation({
    mutationKey: ['/reprocess-source'],
    mutationFn: (sourceId: string) => {
      return axiosClient.post(`/projects/sources/${sourceId}/reprocess`)
    },
  })
}

/**
 * Delete an article from a project
 */
export function useDeleteArticle() {
  return useMutation({
    mutationKey: ['/delete-article'],
    mutationFn: (articleId: string) => {
      return axiosClient.delete(`/medinsight/articles/${articleId}`)
    },
  })
}
